import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from 'primereact/skeleton';
import { labelGenerationApi } from 'api';
import { RouterUrlParams } from 'App';
import { Application } from 'types/Application';
import { usePanel } from 'contexts/panelContext';
import { Page } from 'components/_new/Page';
import { Box } from 'components/_new/Box';
import { ArticlePresenter } from 'components/knowledgeBase/ArticlePresenter';
import { KnowledgeBaseArticlePresentationResult } from 'types/KnowledgeBase';
import { MessageBox } from 'components/_new/MessageBox';
import hourglassIcon from 'assets/hourglassOutlined.svg';
import dummyImage from 'assets/dummy.png';

interface Props {
	data: Application;
}

const LinkedInCard: FC<Props> = ({ data }) => {
	const { t } = useTranslation();

	const { organizationName, programName } = useParams() as RouterUrlParams;

	const { currentApplicationData, panelLinkPrefix, panelType } = usePanel();

	const { data: checkIsFileResult, isLoading } = useQuery(
		['label-check', currentApplicationData.id],
		() => labelGenerationApi.checkLabel(currentApplicationData.id),
		{
			enabled: true && Boolean(currentApplicationData.id),
			select: (data) => {
				return data as unknown as KnowledgeBaseArticlePresentationResult;
			},
		}
	);

	if (isLoading) {
		return (
			<Page>
				<Box className="p-4">
					<div className="flex flex-column lg:flex-row gap-4">
						<div style={{ flex: '2' }}>
							<Skeleton width="100%" height="3.5rem" className="mb-4" borderRadius="8px" />
							<div className="mb-4">
								<Skeleton width="100%" height="1rem" className="mb-2" />
								<Skeleton width="92%" height="1rem" />
							</div>
							<div className="mb-4">
								<Skeleton width="100%" height="1rem" className="mb-2" />
								<Skeleton width="98%" height="1rem" className="mb-2" />
								<Skeleton width="85%" height="1rem" />
							</div>
							<div className="mb-4">
								<Skeleton width="100%" height="1rem" className="mb-2" />
								<Skeleton width="60%" height="1rem" />
							</div>

							<Skeleton width="35%" height="1.8rem" className="mb-3" />

							<div className="flex flex-column gap-2 mb-4">
								<Skeleton width="90%" height="1rem" />
								<Skeleton width="85%" height="1rem" />
								<Skeleton width="95%" height="1rem" />
								<Skeleton width="100%" height="1rem" />
							</div>

							<div className="flex gap-4 mb-4">
								<div className="flex-1">
									<Skeleton width="80%" height="1rem" className="mb-2" />
									<Skeleton width="90%" height="1rem" className="mb-2" />
								</div>
								<div className="flex-1">
									<Skeleton width="85%" height="1rem" className="mb-2" />
									<Skeleton width="75%" height="1rem" className="mb-2" />
								</div>
							</div>

							<div className="mb-4">
								<Skeleton width="25%" height="1rem" />
							</div>

							<div className="flex gap-4">
								<div className="flex-1">
									<Skeleton width="75%" height="1rem" />
								</div>
								<div className="flex-1">
									<Skeleton width="70%" height="1rem" />
								</div>
							</div>
						</div>

						<div className="hidden lg:flex justify-content-center align-items-start" style={{ flex: '1' }}>
							<Skeleton width="300px" height="300px" borderRadius="10px" />
						</div>
					</div>
				</Box>
			</Page>
		);
	}

	const shouldShowPlaceholder = !checkIsFileResult?.article;

	if (shouldShowPlaceholder) {
		return (
			<div className="flex flex-column gap-4 lg:flex-row">
				<Box className="w-full flex flex-column p-4" style={{ flex: '2' }}>
					<MessageBox variant="rose" message={t('userPanel.linkedInBadge.notAvailableYet')} />

					<div className="flex align-items-center justify-content-center" style={{ minHeight: '200px' }}>
						<img src={hourglassIcon} alt="Hourglass" style={{ width: '80px', height: '80px' }} />
					</div>

					<p className=" m-0">{t('userPanel.linkedInBadge.waitForNotification')}</p>
				</Box>

				<Box
					noPad
					className="hidden lg:flex justify-content-center align-items-center mr-1"
					style={{ flex: '1', position: 'relative' }}
				>
					<img
						src={dummyImage}
						alt="LinkedIn Badge Preview"
						style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
					/>
					<div
						style={{
							position: 'absolute',
							bottom: '30px',
							left: '50%',
							transform: 'translateX(-50%)',
							textAlign: 'start',
							width: '85%',
						}}
					>
						<p
							className="m-0 text-white"
							style={{
								fontSize: 'clamp(20px, 5vw, 28px)',
								fontWeight: '500',
								lineHeight: '1.1',
								letterSpacing: '-0.5px',
								fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
							}}
						>
							{t('userPanel.linkedInBadge.participating')}
						</p>
						<p
							className="m-0 text-white"
							style={{
								fontSize: 'clamp(13px, 3vw, 15px)',
								fontWeight: '400',
								lineHeight: '1.3',
								marginTop: '4px',
								opacity: '0.95',
								fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
							}}
						>
							{t('userPanel.linkedInBadge.inMentoringProgram')}
						</p>
					</div>
				</Box>
			</div>
		);
	}

	return (
		<Page>
			{checkIsFileResult?.article && (
				<ArticlePresenter
					article={checkIsFileResult.article}
					articleContent={checkIsFileResult.articleContent!}
					options={{ disableRating: true }}
					contentParserOptions={{
						replaces: {
							panelPrefix: panelLinkPrefix,
							organization: organizationName,
							program: programName,
							usertype: panelType,
						},
						disableFormat: true,
					}}
				/>
			)}
		</Page>
	);
};

export default LinkedInCard;
